import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  UsersThree, UserPlus, Trash, Key, CircleNotch, Copy, X, ShieldCheck, WarningCircle, Sparkle,
} from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLES, ROLE_LABELS, ROLE_DESCRIPTIONS } from "@/lib/permissions";

const ROLE_BADGE = {
  superadmin: "bg-[#0033FF] text-white",
  admin: "bg-[#0033FF]/10 text-[#0033FF]",
  redaktör: "bg-emerald-100 text-emerald-700",
  handläggare: "bg-amber-100 text-amber-700",
  läsare: "bg-zinc-100 text-zinc-600",
};

function TempPasswordDialog({ data, onClose }) {
  if (!data) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="temp-password-dialog">
      <div className="w-full max-w-md bg-white rounded-2xl border border-zinc-200 p-6 shadow-xl">
        <div className="flex items-start justify-between">
          <h3 className="font-display font-bold text-xl">{data.demo ? "Demokonto skapat" : "Temporärt lösenord"}</h3>
          <button onClick={onClose} data-testid="close-temp-dialog" className="text-zinc-400 hover:text-zinc-700"><X size={20} weight="bold" /></button>
        </div>
        <p className="mt-1 text-sm text-zinc-500">{data.demo
          ? <>Logga in med <strong>{data.email}</strong> och lösenordet nedan. Demokontot har begränsade behörigheter.</>
          : <>Dela detta med <strong>{data.email}</strong>. De ombeds välja eget lösenord vid första inloggningen.</>}</p>
        <div className="mt-4 flex items-center gap-2 bg-zinc-50 border border-zinc-200 rounded-lg px-4 py-3">
          {data.demo && <span className="text-xs text-zinc-400 font-semibold shrink-0">E-post:</span>}
          {data.demo && <code className="text-sm font-mono text-zinc-600 truncate">{data.email}</code>}
        </div>
        <div className="mt-2 flex items-center gap-2 bg-zinc-50 border border-zinc-200 rounded-lg px-4 py-3">
          {data.demo && <span className="text-xs text-zinc-400 font-semibold shrink-0">Lösenord:</span>}
          <code className="flex-1 text-lg font-mono font-bold text-[#0033FF] tracking-wide" data-testid="temp-password-value">{data.temp_password}</code>
          <button onClick={() => { navigator.clipboard.writeText(data.temp_password); toast.success("Kopierat"); }}
            className="text-zinc-500 hover:text-[#0033FF]" data-testid="copy-temp-password"><Copy size={20} weight="bold" /></button>
        </div>
        {data.demo ? (
          <div className="mt-4 flex items-start gap-2 text-sm rounded-lg px-3 py-2 bg-blue-50 text-blue-700">
            <WarningCircle size={18} weight="bold" className="mt-0.5 shrink-0" />
            <span>Spara uppgifterna nu — lösenordet visas bara denna gång.</span>
          </div>
        ) : (
          <div className={`mt-4 flex items-start gap-2 text-sm rounded-lg px-3 py-2 ${data.email_sent ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
            <WarningCircle size={18} weight="bold" className="mt-0.5 shrink-0" />
            <span>{data.email_sent ? "Ett mejl med lösenordet har skickats." : "Mejl kunde inte skickas automatiskt — dela lösenordet manuellt. (Verifiera en domän i Resend för att mejla alla adresser.)"}</span>
          </div>
        )}
        <button onClick={onClose} data-testid="temp-dialog-done" className="mt-5 w-full bg-[#0033FF] hover:bg-[#002BE6] text-white rounded-md px-4 py-2.5 font-semibold transition-colors">Klar</button>
      </div>
    </div>
  );
}

export default function Team() {
  const { user, can } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [tempData, setTempData] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", role: "läsare" });

  const canManage = can("manage_users");
  const isSuper = user?.role === "superadmin";
  const assignableRoles = ROLES.filter((r) => r !== "superadmin" || isSuper);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kunde inte hämta användare");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (canManage) load(); else setLoading(false); }, [canManage, load]);

  const invite = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/users", form);
      setTempData({ email: data.user.email, temp_password: data.temp_password, email_sent: data.email_sent });
      setForm({ name: "", email: "", role: "läsare" });
      toast.success("Användare skapad");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kunde inte skapa användare");
    } finally {
      setBusy(false);
    }
  };

  const createDemo = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/users/demo", {});
      setTempData({ email: data.email, temp_password: data.temp_password, demo: true });
      toast.success("Demokonto skapat");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kunde inte skapa demokonto");
    } finally {
      setBusy(false);
    }
  };

  const changeRole = async (u, role) => {
    try {
      await api.patch(`/users/${u.id}`, { role });
      toast.success(`Roll uppdaterad till ${ROLE_LABELS[role]}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Misslyckades");
    }
  };

  const toggleStatus = async (u) => {
    const status = u.status === "active" ? "suspended" : "active";
    try {
      await api.patch(`/users/${u.id}`, { status });
      toast.success(status === "active" ? "Konto aktiverat" : "Konto inaktiverat");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Misslyckades");
    }
  };

  const resetPassword = async (u) => {
    try {
      const { data } = await api.post(`/users/${u.id}/reset-password`);
      setTempData({ email: u.email, temp_password: data.temp_password, email_sent: data.email_sent });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Misslyckades");
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Ta bort ${u.email}? Detta kan inte ångras.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Användare borttagen");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Misslyckades");
    }
  };

  if (!canManage) {
    return (
      <div className="p-6 sm:p-8 max-w-2xl" data-testid="team-no-access">
        <div className="bg-white border border-zinc-200 rounded-xl p-8 text-center">
          <ShieldCheck size={32} weight="bold" className="text-zinc-300 mx-auto" />
          <h2 className="mt-3 font-display font-bold text-xl">Ingen behörighet</h2>
          <p className="mt-1 text-sm text-zinc-500">Endast Super Admin och Admin kan hantera teamet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 sm:p-8 max-w-5xl" data-testid="team-page">
      <TempPasswordDialog data={tempData} onClose={() => setTempData(null)} />
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-[#0033FF] flex items-center justify-center">
          <UsersThree size={22} weight="bold" className="text-white" />
        </div>
        <div>
          <h1 className="font-display font-bold text-3xl tracking-tight">Team & roller</h1>
          <p className="mt-0.5 text-zinc-500 text-sm">Bjud in medarbetare och styr deras behörigheter.</p>
        </div>
      </div>

      {/* Invite */}
      <div className="mt-7 bg-white border border-zinc-200 rounded-xl p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h3 className="font-display font-bold text-lg flex items-center gap-2"><UserPlus size={20} weight="bold" className="text-[#0033FF]" /> Bjud in användare</h3>
          <button onClick={createDemo} disabled={busy} data-testid="create-demo-button"
            className="flex items-center gap-2 border border-[#0033FF]/30 text-[#0033FF] hover:bg-[#0033FF]/5 rounded-md px-3.5 py-2 text-sm font-semibold transition-colors disabled:opacity-60">
            {busy ? <CircleNotch size={16} className="animate-spin" /> : <Sparkle size={16} weight="bold" />} Skapa demokonto
          </button>
        </div>
        <form onSubmit={invite} className="mt-4 grid sm:grid-cols-[1fr_1fr_160px_auto] gap-3 items-end">
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">Namn</label>
            <input data-testid="invite-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required
              className="mt-1.5 w-full bg-white border border-zinc-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0033FF]/20 focus:border-[#0033FF] outline-none" placeholder="Anna Andersson" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">E-post</label>
            <input data-testid="invite-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required
              className="mt-1.5 w-full bg-white border border-zinc-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0033FF]/20 focus:border-[#0033FF] outline-none" placeholder="anna@företag.se" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">Roll</label>
            <select data-testid="invite-role-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="mt-1.5 w-full bg-white border border-zinc-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0033FF]/20 focus:border-[#0033FF] outline-none">
              {assignableRoles.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
            </select>
          </div>
          <button type="submit" disabled={busy} data-testid="invite-submit-button"
            className="flex items-center justify-center gap-2 bg-[#0033FF] hover:bg-[#002BE6] text-white rounded-md px-5 py-2 text-sm font-semibold transition-colors disabled:opacity-60 h-[38px]">
            {busy ? <CircleNotch size={16} className="animate-spin" /> : <UserPlus size={16} weight="bold" />} Skapa
          </button>
        </form>
        <p className="mt-3 text-xs text-zinc-400">{ROLE_DESCRIPTIONS[form.role]}</p>
      </div>

      {/* Users table */}
      <div className="mt-6 bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3.5 border-b border-zinc-200 text-sm font-semibold text-zinc-500">
          {loading ? "Laddar…" : `${users.length} användare`}
        </div>
        {loading ? (
          <div className="p-10 flex justify-center"><CircleNotch size={24} className="animate-spin text-[#0033FF]" /></div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {users.map((u) => {
              const isSelf = u.id === user?.id;
              const isTargetSuper = u.role === "superadmin";
              const lockManage = isTargetSuper && !isSuper; // non-super can't touch super
              return (
                <div key={u.id} className="px-5 py-4 flex flex-wrap items-center gap-3" data-testid={`user-row-${u.email}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm text-zinc-900 flex items-center gap-2">
                      {u.name || "—"}
                      {isSelf && <span className="text-[11px] font-bold text-zinc-400">(du)</span>}
                      {u.is_demo && <span className="text-[11px] font-bold text-amber-600 bg-amber-50 rounded px-1.5 py-0.5">DEMO</span>}
                    </div>
                    <div className="text-xs text-zinc-500 truncate">{u.email}</div>
                  </div>

                  <span className={`text-[11px] font-bold rounded-full px-2.5 py-1 ${u.status === "active" ? "bg-emerald-50 text-emerald-600" : u.status === "pending" ? "bg-amber-50 text-amber-600" : "bg-red-50 text-red-600"}`}>
                    {u.status === "active" ? "Aktiv" : u.status === "pending" ? "Väntar" : "Inaktiv"}
                  </span>

                  {lockManage ? (
                    <span className={`text-xs font-bold rounded-full px-3 py-1.5 ${ROLE_BADGE[u.role]}`}>{ROLE_LABELS[u.role]}</span>
                  ) : (
                    <select data-testid={`role-select-${u.email}`} value={u.role} disabled={isSelf}
                      onChange={(e) => changeRole(u, e.target.value)}
                      className="text-xs font-semibold bg-white border border-zinc-200 rounded-md px-2 py-1.5 focus:ring-2 focus:ring-[#0033FF]/20 outline-none disabled:opacity-60">
                      {assignableRoles.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                    </select>
                  )}

                  <div className="flex items-center gap-1.5">
                    {!lockManage && !isSelf && (
                      <button onClick={() => toggleStatus(u)} data-testid={`toggle-status-${u.email}`}
                        className="text-xs font-semibold text-zinc-500 hover:text-zinc-900 border border-zinc-200 rounded-md px-2.5 py-1.5">
                        {u.status === "active" ? "Inaktivera" : "Aktivera"}
                      </button>
                    )}
                    {!lockManage && (
                      <button onClick={() => resetPassword(u)} title="Återställ lösenord" data-testid={`reset-pw-${u.email}`}
                        className="text-zinc-400 hover:text-[#0033FF] p-1.5"><Key size={17} weight="bold" /></button>
                    )}
                    {isSuper && !isSelf && !isTargetSuper && (
                      <button onClick={() => removeUser(u)} title="Ta bort" data-testid={`delete-user-${u.email}`}
                        className="text-zinc-400 hover:text-red-600 p-1.5"><Trash size={17} weight="bold" /></button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
